import type { Breadcrumb, NavigationItem } from '@/shared/lib';
import type {
  VisibleDirectoryItem,
  VisibleDirectoryTree,
  VisibleFolderEntry,
  VisibleFolderNode,
  VisibleFolderPathSegment,
  VisibleModuleNode,
} from './visible-tree.types';

const CUSTOM_FOLDER_CODE_PATTERN = /^F(\d+)$/;
const MODULE_CODE_PATTERN = /^M(\d+)$/;

export function toModuleNavigationItemId(moduleId: number): string {
  return String(moduleId);
}

export function toModuleFolderNavigationItemId(folderId: number): string {
  return `module-folder-${folderId}`;
}

export function toCustomFolderCode(folderId: number): string {
  return `F${folderId}`;
}

export function parseCustomFolderCode(code: string): number | null {
  const match = CUSTOM_FOLDER_CODE_PATTERN.exec(code);
  return match ? Number(match[1]) : null;
}

export function isCustomFolderCode(code: string): boolean {
  return parseCustomFolderCode(code) !== null;
}

export function toModuleCode(moduleId: number): string {
  return `M${moduleId}`;
}

export function parseModuleCode(code: string): number | null {
  const match = MODULE_CODE_PATTERN.exec(code);
  return match ? Number(match[1]) : null;
}

export function isModuleCode(code: string): boolean {
  return parseModuleCode(code) !== null;
}

export function isDirectoryContainerCode(code: string): boolean {
  return isCustomFolderCode(code) || isModuleCode(code);
}

function mapFolderToNavigationItem(folder: VisibleFolderNode, moduleId: number): NavigationItem {
  return {
    id: toModuleFolderNavigationItemId(folder.id),
    name: folder.name,
    code: toCustomFolderCode(folder.id),
    type: 'category',
    icon: 'folder',
    expanded: false,
    children: folder.children.map((child) => mapFolderToNavigationItem(child, moduleId)),
    metadata: {
      folderId: folder.id,
      moduleId,
      listable: true,
      contentKind: folder.contentKind,
      matrixId: folder.matrixId ?? undefined,
    },
  };
}

export function mapVisibleModulesToNavigation(tree: VisibleDirectoryTree): NavigationItem[] {
  return tree.modules.map((module) => ({
    id: toModuleNavigationItemId(module.moduleId),
    name: module.moduleName,
    code: toModuleCode(module.moduleId),
    type: 'module' as const,
    icon: 'module' as const,
    expanded: false,
    children: module.folders.map((folder) => mapFolderToNavigationItem(folder, module.moduleId)),
    metadata: {
      moduleId: module.moduleId,
      iconId: module.iconId,
      listable: true,
    },
  }));
}

function walkFolders(
  folders: VisibleFolderNode[],
  visit: (folder: VisibleFolderNode, path: VisibleFolderPathSegment[]) => void,
  parentPath: VisibleFolderPathSegment[] = []
): void {
  for (const folder of folders) {
    const path = [...parentPath, { id: folder.id, name: folder.name }];
    visit(folder, path);
    walkFolders(folder.children, visit, path);
  }
}

export function mapVisibleTreeToDocumentTypes(tree: VisibleDirectoryTree): NavigationItem[] {
  return tree.fixedFolders
    .filter((folder) => !!folder.prefixCode)
    .map((folder) => ({
      id: `folder-${folder.id}`,
      name: folder.name,
      code: folder.prefixCode ?? undefined,
      type: 'document' as const,
      icon: 'folder' as const,
      expanded: false,
      children: [],
      metadata: { folderId: folder.id },
    }));
}

export function buildFolderIndex(tree: VisibleDirectoryTree): Map<number, VisibleFolderEntry> {
  const index = new Map<number, VisibleFolderEntry>();

  walkFolders(tree.fixedFolders, (folder, path) => {
    index.set(folder.id, { folder, moduleId: null, moduleName: null, path });
  });

  for (const module of tree.modules) {
    walkFolders(module.folders, (folder, path) => {
      index.set(folder.id, {
        folder,
        moduleId: module.moduleId,
        moduleName: module.moduleName,
        path,
      });
    });
  }

  return index;
}

export function buildFolderCodeIndex(
  tree: VisibleDirectoryTree,
  folderIndex: Map<number, VisibleFolderEntry> = buildFolderIndex(tree)
): Map<string, VisibleFolderEntry> {
  const index = new Map<string, VisibleFolderEntry>();

  for (const entry of folderIndex.values()) {
    index.set(toCustomFolderCode(entry.folder.id), entry);
  }

  for (const folder of tree.fixedFolders) {
    const entry = folderIndex.get(folder.id);
    if (folder.prefixCode && entry && !index.has(folder.prefixCode)) {
      index.set(folder.prefixCode, entry);
    }
  }

  return index;
}

export function buildModuleCodeIndex(tree: VisibleDirectoryTree): Map<string, VisibleModuleNode> {
  return new Map(tree.modules.map((module) => [toModuleCode(module.moduleId), module]));
}

export function getContainerMatrices(container: {
  items?: VisibleDirectoryItem[];
}): VisibleDirectoryItem[] {
  return (container.items ?? []).filter((item) => item.itemType === 'MATRIX');
}

export function buildFolderBreadcrumbTrail(
  entry: VisibleFolderEntry,
  currentPath: string
): Breadcrumb[] {
  const ancestors = entry.path.slice(0, -1);
  const moduleCrumb =
    entry.moduleId !== null && entry.moduleName
      ? [{ name: entry.moduleName, path: `/${toModuleCode(entry.moduleId)}` }]
      : [];

  return [
    ...moduleCrumb,
    ...ancestors.map((segment) => ({
      name: segment.name,
      path: `/${toCustomFolderCode(segment.id)}`,
    })),
    { name: entry.folder.name, path: currentPath, clickable: false },
  ];
}

export function buildModuleBreadcrumbTrail(
  module: VisibleModuleNode,
  currentPath: string
): Breadcrumb[] {
  return [{ name: module.moduleName, path: currentPath, clickable: false }];
}
